import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import { requireAuth } from "utilities/auth";
import { useSlaPolicies } from "hooks/useSlaPolicies";
import { useRequestTypesAdmin } from "hooks/useRequestTypesAdmin";
import { requestTypeLabeler } from "utilities/requestTypes";
import { TICKET_PRIORITY_LABELS } from "constants/tickets";
import { formatTargetMins } from "utilities/sla";
import { Timer, Plus, Search, Globe } from "lucide-react";

/**
 * SlaPolicies (SOUND-534) — the SLA policy catalog + coverage view. One row per
 * policy; a global `{}` default (both match dims "any") applies to everything
 * until a more specific policy is authored. Mirrors the Workflows / Notification
 * Config settings pattern (list → editor route). Internal-only.
 */
function SlaPolicies() {
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const { policies, loading } = useSlaPolicies({ includeInactive: showInactive });
  // Admin vocabulary incl. deactivated types, so a policy on one still reads by
  // its label (SOUND-785 ride-along; /tickets/request-types omits them).
  const { requestTypes } = useRequestTypesAdmin({ includeInactive: true });
  const [search, setSearch] = useState("");

  const rtLabel = useMemo(() => requestTypeLabeler(requestTypes), [requestTypes]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return policies.filter(
      (p) => !q || (p.name || "").toLowerCase().includes(q)
    );
  }, [policies, search]);

  return (
    <div>
      <PageHeader
        title="SLA Policies"
        subtitle="First-response + resolution targets, matched by priority × request type. The most specific active policy wins; a global default covers everything else. Clock is 24×7."
        actions={
          <button className="btn btn-primary" onClick={() => navigate("/settings/sla-policies/new")}>
            <Plus className="h-4 w-4" />
            New policy
          </button>
        }
      />

      <div className="container space-y-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className="input pl-9"
              placeholder="Search policies…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
        </div>

        {loading ? (
          <div className="space-y-3">
            <Skeleton.Rect className="h-12 w-full" />
            <Skeleton.Rect className="h-12 w-full" />
            <Skeleton.Rect className="h-12 w-2/3" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Timer}
            heading="No SLA policies yet"
            description="Create a global default first, then add priority- or request-type-specific overrides."
            action={
              <button className="btn btn-primary" onClick={() => navigate("/settings/sla-policies/new")}>
                <Plus className="h-4 w-4" />
                New policy
              </button>
            }
          />
        ) : (
          <div className="surface overflow-hidden rounded-xl ring-1 ring-border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {["Policy", "Applies to", "First response", "Resolution", "Status"].map((c) => (
                      <th
                        key={c}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((p) => {
                    const isGlobal = p.match?.priority == null && p.match?.requestType == null;
                    return (
                      <tr
                        key={p.policyId}
                        className="cursor-pointer transition-colors hover:bg-muted"
                        onClick={() => navigate(`/settings/sla-policies/${p.policyId}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{p.name}</div>
                        </td>
                        <td className="px-4 py-3">
                          {isGlobal ? (
                            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                              <Globe className="h-3.5 w-3.5" />
                              Global default
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              <span className="badge badge-muted">
                                {p.match?.priority
                                  ? TICKET_PRIORITY_LABELS[p.match.priority] || p.match.priority
                                  : "Any priority"}
                              </span>
                              <span className="badge badge-muted">
                                {p.match?.requestType ? rtLabel(p.match.requestType) : "Any type"}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatTargetMins(p.firstResponseTargetMins)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatTargetMins(p.resolutionTargetMins)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`badge ${p.active ? "badge-success" : "badge-muted"}`}>
                            {p.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(SlaPolicies);
